import { BaseModel } from './baseModel';
import { Cell } from './cell';
import type { CellStyle } from './cellStyle';
import type { RectRange } from './range';
import { plainText } from './richText';
import type { Sheet } from './sheet';
import { DEFAULT_TABLE_STYLE, type TableStyle } from './tableStyle';
import { type StoredStyle, type StyleToMerge, mergeStyles } from './types';
import type { Vector2 } from '@/utils/vec';
import { nanoid } from 'nanoid';

export class Table extends BaseModel {
  constructor(
    public readonly name: string,
    public readonly range: RectRange,
    public readonly style: StoredStyle<TableStyle> = DEFAULT_TABLE_STYLE,
    public readonly headers: string[] | null = null,
    public readonly id = nanoid(),
  ) {
    super();
  }

  public withName(name: string): Table {
    return new Table(name, this.range, this.style, this.headers, this.id);
  }

  public withRange(range: RectRange): Table {
    return new Table(this.name, range, this.style, this.headers, this.id);
  }

  public withStyle(style: StyleToMerge<TableStyle>): Table {
    return new Table(
      this.name,
      this.range,
      mergeStyles(this.style, style),
      this.headers,
      this.id,
    );
  }

  public withHeaders(headers: string[]): Table {
    return new Table(this.name, this.range, this.style, headers, this.id);
  }

  public getOrCreateHeaderCells(sheet: Sheet): Cell[] {
    const poses = this.range.getHeaderPoses(this.getOrientation() === 'col');

    const cells: Cell[] = [];
    for (const pos of poses) {
      const cell = (sheet.getCellAt(pos) ?? new Cell(pos)).withDefaultOrValue(
        'Header ' + pos.s,
      );
      sheet.setCell(cell);
      cells.push(cell);
    }
    return cells;
  }

  public withCreateHeaders(sheet: Sheet): Table {
    const cells = this.getOrCreateHeaderCells(sheet);
    return this.withHeaders(cells.map(c => plainText(c.value)));
  }

  public getOrientation(): 'row' | 'col' {
    // TODO: add a way to specify column or row headers for RectRange
    if (this.range.isRowRange()) {
      return 'row';
    }
    return 'col';
  }

  public getParity(pos: Vector2): boolean {
    if (this.getOrientation() === 'row') {
      return (pos.x - this.range.min.x) % 2 === 0;
    }
    return (pos.y - this.range.min.y) % 2 === 0;
  }

  public getStyle(pos: Vector2): StoredStyle<CellStyle> {
    // TODO: more styles than just color
    if ((this.headers?.length ?? 0) > 0) {
      const header = this.range.getHeaderPoses(this.getOrientation() === 'col');
      if (header.some(h => h.equals(pos))) {
        return { background: this.style.headerColor };
      }
      if (this.getParity(pos)) {
        return { background: this.style.secondaryColor };
      }
      return { background: this.style.primaryColor };
    }
    if (this.getParity(pos)) {
      return { background: this.style.primaryColor };
    }
    return { background: this.style.secondaryColor };
  }
}
